import { useState, useEffect } from 'react';
import api from '../lib/api';
import { detectCountryHint } from '../lib/razorpay';

/**
 * Billing currency hook (SEPARATE from the CRM CurrencyContext).
 *
 * Lockene billing values (credit balance, per-action usage cost, packs, plans)
 * are stored as USD floats. For Indian/Razorpay tenants they must be displayed in
 * ₹ using the live usd_inr_rate from /gateway-config — NOT just relabelled.
 * Never route these through the CRM `useCurrency()` (that's the tenant's own
 * business currency, a different concept).
 *
 * Fails open to USD on any error so dashboards never crash.
 */
export function useBillingCurrency() {
  const [cfg, setCfg] = useState({ currency: 'USD', usdInrRate: 0, gstRatePct: 0 });

  useEffect(() => {
    let active = true;
    // Pass the browser-timezone country hint (same as Billing.js) so the backend
    // can detect India — edge headers are stripped, so without this it defaults to USD.
    const hint = detectCountryHint();
    api.get(`/api/tenant/billing/gateway-config${hint ? `?country=${hint}` : ''}`)
      .then((res) => {
        if (!active) return;
        setCfg({
          currency: res.data?.currency || 'USD',
          usdInrRate: res.data?.usd_inr_rate || 0,
          gstRatePct: res.data?.gst_rate_pct || 0,
        });
      })
      .catch(() => { /* keep USD default */ });
    return () => { active = false; };
  }, []);

  const format = (usdAmount, { maxFractionDigits = 2 } = {}) => {
    const n = Number(usdAmount) || 0;
    if (cfg.currency === 'INR' && cfg.usdInrRate) {
      return `₹${(n * cfg.usdInrRate).toLocaleString('en-IN', { maximumFractionDigits: maxFractionDigits })}`;
    }
    return `$${n.toFixed(maxFractionDigits)}`;
  };

  return { ...cfg, format };
}
